import { Controller } from "@hotwired/stimulus";

export default class extends Controller {

  connect() {
    this.isDragging = false
    this.startX = 0
    this.startY = 0
    this.offsetX = 0
    this.offsetY = 0
    this.baseX = 0
    this.baseY = 0
    this.deltaX = 0
    this.deltaY = 0
    this.hasMoved = false

    this.move = this.move.bind(this)
    this.stop = this.stop.bind(this)

    this.element.addEventListener('mousedown', (e) => this.start(e))
  }

  disconnect() {
    this.element.removeEventListener('mousedown', this.start)
    window.removeEventListener('mousemove', this.move)
    window.removeEventListener('mouseup', this.stop)
  }

  start(event) {
    // Voorkom slepen als er op resize/rotate handles wordt geklikt
    if (event.target.closest('.editor__resize-handle') || event.target.closest('.editor__rotate-handle')) {
      return
    }

    // Check of de klik binnen dit element is (inclusief child elementen zoals img of span)
    const clickedElement = event.target.closest('.editor__editable-element')
    if (clickedElement !== this.element) {
      return
    }

    // Bij tekst: slepen start ook als je op de span begint; pas na 3px beweging tellen we als slepen,
    // anders geldt de klik als selectie/bewerking (zie move() en stop()).

    // Voor niet-tekstelementen: voorkom standaard selectie / slepen (afbeeldingen, shapes, etc.)
    const img = this.element.querySelector('img')
    const elementType = this.element.dataset.elementType
    if (img) {
      // Specifiek: standaard browser \"afbeelding slepen\" uitschakelen
      img.draggable = false
      event.preventDefault()
    } else if (elementType && elementType !== 'text') {
      // Andere niet-tekst elementen (zoals shapes): voorkom tekstselectie op achtergrond
      event.preventDefault()
    }

    // Verander cursor naar grabbing bij mousedown
    this.element.style.cursor = 'grabbing'

    const canvas = this.element.closest('.editor__canvas')
    if (!canvas) return

    const elementRect = this.element.getBoundingClientRect()
    const canvasRect = canvas.getBoundingClientRect()

    const scale = parseFloat(canvas.dataset.scale) || 1
    // Bepaal de huidige basispositie in logische canvas-pixels (1920x1080)
    let currentLeft = this.element.style.left
    let currentTop = this.element.style.top

    if (currentLeft) {
      this.baseX = parseFloat(currentLeft) || 0
    } else {
      this.baseX = (elementRect.left - canvasRect.left) / scale
    }

    if (currentTop) {
      this.baseY = parseFloat(currentTop) || 0
    } else {
      this.baseY = (elementRect.top - canvasRect.top) / scale
    }

    // Sla op waar in het element geklikt is
    this.offsetX = event.clientX - elementRect.left
    this.offsetY = event.clientY - elementRect.top

    this.startX = event.clientX
    this.startY = event.clientY
    this.canvas = canvas
    this.canvasRect = canvasRect
    this.elementWidth = elementRect.width
    this.elementHeight = elementRect.height
    this.deltaX = 0
    this.deltaY = 0
    this.isDragging = false
    this.hasMoved = false

    // Bewaar bestaande rotate uit transform (bijv. na eerdere rotatie)
    const existingTransform = this.element.style.transform || ''
    const rotateMatch = existingTransform.match(/rotate\(([-\d.]+)deg\)/)
    this.existingRotate = rotateMatch ? `rotate(${rotateMatch[1]}deg)` : ''

    this.element.style.willChange = 'transform'
    this.element.style.transform = this.existingRotate

    window.addEventListener('mousemove', this.move)
    window.addEventListener('mouseup', this.stop)
  }

  move(event) {
    // Bij tekstelementen: voorkom tekstselectie zodra er bewogen wordt (ook vóór 3px)
    if (this.element.dataset.elementType === 'text') {
      event.preventDefault()
    }
    // Check of er daadwerkelijk bewogen is (minimaal 3px)
    if (!this.hasMoved) {
      const deltaX = Math.abs(event.clientX - this.startX)
      const deltaY = Math.abs(event.clientY - this.startY)
      if (deltaX < 3 && deltaY < 3) {
        return
      }
      // Start slepen - markeer dat er bewogen is
      this.hasMoved = true
      this.isDragging = true
      this.element.classList.add('editor__dragging')
      this.element.dataset.wasDragged = 'true'
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.isDragging) return

    // Update canvas rect (kan veranderd zijn door scroll)
    const currentCanvasRect = this.canvas.getBoundingClientRect()
    if (Math.abs(currentCanvasRect.left - this.canvasRect.left) > 1 ||
      Math.abs(currentCanvasRect.top - this.canvasRect.top) > 1) {
      this.canvasRect = currentCanvasRect
    }

    const scale = parseFloat(this.canvas.dataset.scale) || 1
    // Nieuwe positie in logische canvas-pixels (1920x1080)
    let pixelX = (event.clientX - this.canvasRect.left - this.offsetX) / scale
    let pixelY = (event.clientY - this.canvasRect.top - this.offsetY) / scale

    const maxPixelX = 1920 - this.elementWidth / scale
    const maxPixelY = 1080 - this.elementHeight / scale
    pixelX = Math.max(0, Math.min(pixelX, maxPixelX))
    pixelY = Math.max(0, Math.min(pixelY, maxPixelY))

    this.deltaX = pixelX - this.baseX
    this.deltaY = pixelY - this.baseY

    // Combineer translate (slepen) met bestaande rotate
    const translate = `translate3d(${this.deltaX}px, ${this.deltaY}px, 0)`
    this.element.style.transform = this.existingRotate ? `${translate} ${this.existingRotate}` : translate
  }

  stop(event) {
    window.removeEventListener('mousemove', this.move)
    window.removeEventListener('mouseup', this.stop)

    const wasDragging = this.isDragging

    if (this.element) {
      // Schrijf definitieve positie terug naar left/top
      this.element.style.left = (this.baseX + this.deltaX) + 'px'
      this.element.style.top = (this.baseY + this.deltaY) + 'px'
      // Herstel bestaande rotate (niet overschrijven met 'none')
      this.element.style.transform = this.existingRotate || ''
      this.element.classList.remove('editor__dragging')
      this.element.style.willChange = 'auto'
      this.element.style.cursor = '' // Reset cursor naar CSS default (grab)
    }

    this.isDragging = false
    this.hasMoved = false
    this.existingRotate = ''
    this.canvas = null
    this.canvasRect = null
    this.deltaX = 0
    this.deltaY = 0

    // Verwijder drag marker na korte delay
    if (this.element.dataset.wasDragged) {
      setTimeout(() => {
        if (this.element) {
          delete this.element.dataset.wasDragged
        }
      }, 100)
    }

    // Voorkom click event als er gesleept is
    if (wasDragging && event) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

}